//******************************************************************************************
// AdminMessagesContextProvider: Manages state and actions for admin interactions with user
// conversations. Listens to socket events for real-time updates on conversations, handles
// message fetching, sending, and dynamic room joining for Botpress conversation management.
//******************************************************************************************

import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useAdminSocket } from "./AdminSocketContext";
import { useAuthContext } from "./AuthContext";

// Create context for managing admin messages and conversation states across components
export const AdminMessagesContext = createContext();

// Custom hook to access the AdminMessagesContext in components
export const useAdminMessagesContext = () => {
  return useContext(AdminMessagesContext);
};

export const AdminMessagesContextProvider = ({ children }) => {
  const { socket, isConnected } = useAdminSocket();
  const { authUser, handleExpiredToken } = useAuthContext();

  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [BotRoomId, setBotRoomId] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const conversationsRef = useRef([]);
  const canCloseConversations = authUser?.canCloseConversations || false;
  const canViewClosed = authUser?.canSeeClosed || false;
  const canSeeOverriddenByOthers = authUser?.canSeeOverriddenByOthers || false;
  const lastNotificationTimeRef = useRef(0);
  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem("admin-token");
      let endpoint = "/api/admin/conversations";

      // Include date parameters if SuperAdmin and either startDate or endDate is provided
      if (authUser?.isSuperAdmin) {
        const params = new URLSearchParams();
        if (startDate) {
          params.append("startDate", startDate);
        }
        if (endDate) {
          params.append("endDate", endDate);
        }
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      }

      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL(endpoint, baseUrl).toString();

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations);
        data.conversations.forEach((conversation) => {
          if (socket && isConnected) {
            data.conversations.filter((c) => !c.closed && c.botRoomId).forEach((c) => socket.emit("joinConversation", c.botRoomId));
          }
        });
      } else {
        console.error("Failed to fetch conversations:", data.message);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  useEffect(() => {
    if (authUser) {
      fetchConversations();
    }
  }, [authUser, socket]);
  useEffect(() => {
    if (authUser) {
      fetchConversations();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (selectedConversation && selectedConversation.botRoomId) {
      setBotRoomId(selectedConversation.botRoomId);
    }
  }, [selectedConversation]);

  const markConversationAsReadLocally = (conversationId, adminId) => {
    if (authUser?._id !== adminId) return;
    setConversations((prev) => prev.map((conv) => (conv._id === conversationId ? { ...conv, adminSeen: true } : conv)));
    if (selectedConversation?._id === conversationId) {
      setSelectedConversation((prev) => (prev ? { ...prev, adminSeen: true } : prev));
    }
  };

  // Function to select a conversation by ID
  const selectConversationById = async (conversationId) => {
    try {
      const token = localStorage.getItem("admin-token");
      const apiUrl = new URL(`/api/config/conversations/${conversationId}`, process.env.REACT_APP_API_URL).toString();

      const response = await fetch(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleExpiredToken();
        return false;
      }

      if (response.status === 404) {
        console.error("Conversation not found");
        return false;
      }

      const data = await response.json();
      if (response.ok && data.success) {
        setSelectedConversation(data.conversation);

        // Mark the conversation as read locally
        setConversations((prev) => prev.map((conv) => (conv._id === conversationId ? { ...conv, adminSeen: true } : conv)));
        return true;
      } else {
        console.error("Failed to fetch conversation details:", data.message);
        return false;
      }
    } catch (error) {
      console.error("Error fetching conversation details:", error);
      return false;
    }
  };

  // Listen for "conversationCreated" events from the socket to dynamically add new conversations
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    const handleConversationCreated = (newConversation) => {
      setConversations((prev) => {
        const updated = [...prev, newConversation];
        conversationsRef.current = updated;
        socket.emit("joinConversation", newConversation.botRoomId);
        return updated;
      });
    };

    socket.on("conversationCreated", handleConversationCreated);
    return () => {
      socket.off("conversationCreated", handleConversationCreated);
    };
  }, [socket, isConnected]);

  // Fetch all conversations when the component mounts or the authenticated user changes
  useEffect(() => {
    const fetchConversationsOnAuth = async () => {
      if (!authUser) return;
      await fetchConversations();
    };
    fetchConversationsOnAuth();
  }, [authUser]);

  // Listen for "conversationUpdated" events to update conversation state in real-time
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleConversationUpdated = (updatedConv) => {
      setConversations((prev) => {
        const exists = prev.find((c) => c._id === updatedConv._id);
        if (exists) {
          return prev.map((c) => (c._id === updatedConv._id ? { ...c, ...updatedConv } : c));
        }
        return [...prev, updatedConv];
      });

      //Check if there was another notification in the last 5 seconds
      const currentTime = Date.now();
      if (currentTime - lastNotificationTimeRef.current < 5000) {
        return;
      }

      if (
        updatedConv._id !== selectedConversation?._id &&
        updatedConv.isOverridden === true &&
        updatedConv.overriddenByAdminId === null &&
        Notification.permission === "granted"
      ) {
        lastNotificationTimeRef.current = currentTime;
        const notification = new Notification("Utilizador à espera de Operador", {
          body: "Conversa está à espera de um operador",
        });
        notification.onclick = () => window.focus();
      }

      // Ensure the last message and last activity are updated
      if (updatedConv.messages && updatedConv.messages.length > 0) {
        setConversations((prevConversations) => {
          return prevConversations.map((conversation) => {
            if (conversation._id === updatedConv._id) {
              const lastMessage = updatedConv.messages[updatedConv.messages.length - 1]; // Get the last message
              return {
                ...conversation,
                lastMessage: {
                  text: lastMessage.message,
                  sender:
                    lastMessage.senderModel === "ChatbotUser"
                      ? "Cidadão"
                      : lastMessage.senderModel === "Admin"
                      ? lastMessage.senderId?.username || "Admin"
                      : "Bot",
                  createdAt: lastMessage.createdAt,
                },
                lastActivity: new Date(lastMessage.createdAt), // Update lastActivity based on the latest message
              };
            }
            return conversation;
          });
        });
      }

      setSelectedConversation((prev) => (prev && prev._id === updatedConv._id ? { ...prev, ...updatedConv } : prev));
    };

    const handleNewMessage = (newMessage) => {
      const { conversationId } = newMessage;
      // Update the messages for the selected conversation if it's the correct one
      if (selectedConversation?._id === conversationId) {
        setMessages((prevMessages) => [...prevMessages, newMessage]);
      }

      // Update the last message for all conversations
      setConversations((prevConversations) => {
        // Get the updated conversation list
        const updatedConversations = prevConversations.map((conversation) => {
          if (conversation._id === conversationId) {
            const lastMessage = newMessage;
            return {
              ...conversation,
              messages: [...conversation.messages, newMessage],
              lastActivity: new Date(),
              lastMessage: {
                text: lastMessage.message,
                sender:
                  lastMessage.senderModel === "ChatbotUser"
                    ? "Cidadão"
                    : lastMessage.senderModel === "Admin"
                    ? lastMessage.senderId?.username || "Admin"
                    : "Bot",
                createdAt: lastMessage.createdAt,
              },
              adminSeen: lastMessage.senderModel === "Admin",
            };
          }
          return conversation;
        });

        return updatedConversations;
      });

      // Handle notifications after state updates
      const conversation = conversations.find((conv) => conv._id === conversationId);
      const currentTime = Date.now();
      // Handle notification if the conversation is assigned to the current admin and not currently selected
      if (
        selectedConversation?._id != conversationId &&
        newMessage.senderModel === "ChatbotUser" &&
        Notification.permission === "granted" &&
        conversation?.overriddenByAdminId === authUser?._id &&
        currentTime - lastNotificationTimeRef.current >= 5000
      ) {
        lastNotificationTimeRef.current = currentTime;
        const notification = new Notification("Nova Mensagem Recebida", {
          body: newMessage.message || "Nova mensagem recebida",
        });
        notification.onclick = () => window.focus();
      }

      // Request notification permission if not already granted
      if (Notification.permission === "default") {
        Notification.requestPermission();
      }
    };
    socket.on("conversationUpdated", handleConversationUpdated);
    socket.on("conversationRemoved", (removedConvId) => {
      setConversations((prev) => prev.filter((c) => c._id !== removedConvId));
      setSelectedConversation((prev) => (prev && prev._id === removedConvId ? null : prev));
    });
    socket.on("newMessage", handleNewMessage);

    return () => {
      socket.off("conversationUpdated", handleConversationUpdated);
      socket.off("newMessage", handleNewMessage);
    };
  }, [socket, isConnected, selectedConversation?._id, authUser?._id, conversations]);
  // Fetch messages for the selected conversation when it changes
  useEffect(() => {
    if (!selectedConversation) return;

    const fetchMessages = async () => {
      const token = localStorage.getItem("admin-token");
      const endpoint = `/api/admin/messages/${selectedConversation._id}/`;
      const apiUrl = new URL(endpoint, process.env.REACT_APP_API_URL).toString();

      try {
        const response = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 401) {
          handleExpiredToken();
          return;
        }

        if (response.status === 403) {
          console.warn("Access denied to messages. Resetting selectedConversation to null.");
          setSelectedConversation(null);
          return;
        }

        const data = await response.json();
        if (response.ok) {
          setMessages(data.messages);

          // Only update selectedConversation if we don't have a botRoomId yet
          setSelectedConversation((prev) => {
            if (!prev) return prev;
            if (!prev.botRoomId && data.conversation?.botRoomId) {
              return {
                ...prev,
                botRoomId: data.conversation.botRoomId,
                overriddenByAdmin: data.conversation.overriddenByAdmin || null,
              };
            }
            return {
              ...prev,
              botRoomId: data.conversation.botRoomId,
              overriddenByAdmin: data.conversation.overriddenByAdmin || null,
            };
          });
        } else {
          if (response.status === 401) {
            handleExpiredToken();
          }
          console.error("Failed to fetch messages:", data.message);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    };

    fetchMessages();
  }, [selectedConversation?._id, handleExpiredToken]);

  return (
    <AdminMessagesContext.Provider
      value={{
        messages,
        setMessages,
        selectedConversation,
        setSelectedConversation,
        selectConversationById,
        conversations,
        setConversations,
        canCloseConversations,
        BotRoomId,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        fetchConversations,
        canViewClosed,
        canSeeOverriddenByOthers,
      }}
    >
      {children}
    </AdminMessagesContext.Provider>
  );
};
